import { ReactElement, useState } from "react";
import { Link } from "react-router-dom";

import { Avatar, Button, Popover } from "@sabre/spark-react-core";

import RenderIf from "@components/common/RenderIf";
import { removeLocalStorageItems } from "@components/data-grid/utils/localStorage.utils";

import {
  API_USER_LOGOUT_URL,
  COLUMN_STATE_STORAGE_PREFIX,
  COMPONENTS,
  ROUTES_LOGOUT,
  ROUTES_PROFILE,
} from "@utils/Constants";
import { checkDomainIsAllowedToGo, getRemoteURL } from "@utils/HttpHelpers";
import useRootLoaderData from "@utils/hooks/useRootLoaderData";

import { useFindComponentVisibility } from "@context/authorization/AuthContext";

interface UserProfileProps {
  userName: string;
  userFullName: string;
}

export interface UserPopoverLinksProps extends UserProfileProps {
  onProfileClick?: () => void;
  isPopoverRestricted?: boolean;
}
function clearUserData(userName: string): void {
  document.cookie = "iPlanetDirectoryPro=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.sabre.com";
  removeLocalStorageItems(`${COLUMN_STATE_STORAGE_PREFIX}-${userName}`);
}

function logoutOnPortal(userName: string): void {
  fetch(getRemoteURL(API_USER_LOGOUT_URL))
    .then((res) => res.text())
    .then((logoutUrl) => {
      clearUserData(userName);
      if (checkDomainIsAllowedToGo(logoutUrl)) {
        window.location.href = logoutUrl + "?goto=" + window.location.href;
      } else {
        console.log("Invalid domain to logout!");
      }
    })
    .catch((error) => {
      console.log(error);
      clearUserData(userName);
    });
}

const UserPopoverLinks = ({
  userName = "",
  userFullName = "",
  onProfileClick,
  isPopoverRestricted = false,
}: UserPopoverLinksProps): ReactElement => {
  const [openPopover, setOpenPopover] = useState(false);
  const { featureFlags } = useRootLoaderData();

  const displayInitials = (name?: string): string => {
    if (!name) {
      return "N/A";
    }
    const nameParts = name.split(" ");
    return nameParts.length === 1
      ? nameParts[0].trim()[0].toUpperCase()
      : `${nameParts[0].trim()[0].toUpperCase()}${nameParts[1].trim()[0].toUpperCase()}`;
  };

  const isProfileVisible = useFindComponentVisibility(COMPONENTS.PROFILE);
  const PopoverContent = (): ReactElement => (
    <>
      <ul className="spark-popover__list">
        <RenderIf condition={isProfileVisible && featureFlags.admin.userPreferences.isVisible}>
          <>
            <li className="spark-popover__list-item">
              <Link to={ROUTES_PROFILE} onClick={() => setOpenPopover(false)} className="spark-popover__list-link">
                Preferences
              </Link>
            </li>
            <hr />
          </>
        </RenderIf>

        <li className="spark-popover__list-item">
          <Link
            className="spark-popover__list-link"
            to={ROUTES_LOGOUT}
            onClick={() => {
              setOpenPopover(false);
              logoutOnPortal(userName);
            }}
          >
            <Button textOnly>Sign out</Button>
          </Link>
        </li>
      </ul>
    </>
  );

  return (
    <Popover
      anchorX="left"
      anchorY="bottom"
      autoFlip={false}
      open={!isPopoverRestricted && openPopover}
      onOpenChange={setOpenPopover}
      toggleEl={
        <a
          href="#"
          className="spark-menu__list-link spark-menu__list-link--avatar"
          onClick={(e): void => {
            e.preventDefault();
            onProfileClick?.();
          }}
        >
          <Avatar text ariaLabel={userFullName}>
            {displayInitials(userFullName)}
          </Avatar>
        </a>
      }
    >
      <PopoverContent />
    </Popover>
  );
};

export default UserPopoverLinks;
